/**
 * App — route table for the Vera operator + review console.
 *
 * Layout: every authenticated route renders inside <AppShell> (AppBar + nav
 * Drawer + sign-out) via nested routes. Each route is wrapped in the right
 * guard: RequireAuth (signed in) then RequireRole (minimum hierarchical role).
 * `/login` is public and outside the shell.
 *
 * Roles (hierarchical reviewer ⊂ operator ⊂ admin):
 *   - operator-only : /rounds, /rounds/:id/operations, /monitor, /exports
 *   - reviewer+     : /rounds/:id (dashboard + expediente browser), /review/:expedienteId
 */

import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { Role } from "./api/client";
import { AppShell } from "./components/AppShell";
import { RequireAuth } from "./components/RequireAuth";
import { RequireRole } from "./components/RequireRole";
import HomeScreen from "./screens/HomeScreen";
import LoginScreen from "./screens/LoginScreen";
import NewExpedienteScreen from "./screens/NewExpedienteScreen";
import RoundsListScreen from "./screens/RoundsListScreen";
import RoundDashboardScreen from "./screens/RoundDashboardScreen";
import ExpedienteDetailScreen from "./screens/ExpedienteDetailScreen";
import OperationsScreen from "./screens/OperationsScreen";
import MonitorScreen from "./screens/MonitorScreen";
import ExportsScreen from "./screens/ExportsScreen";

/** Wrap a screen in the minimum-role guard. */
function Guarded({ role, children }: { role: Role; children: ReactNode }) {
  return <RequireRole role={role}>{children}</RequireRole>;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginScreen />} />

      {/* Authenticated app — shared AppShell layout */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Home — reviewer+ */}
        <Route
          path="/"
          element={
            <Guarded role="reviewer">
              <HomeScreen />
            </Guarded>
          }
        />

        {/* Nuevo expediente — operator */}
        <Route
          path="/expedientes/nuevo"
          element={
            <Guarded role="operator">
              <NewExpedienteScreen />
            </Guarded>
          }
        />

        {/* Rounds list — operator */}
        <Route
          path="/rounds"
          element={
            <Guarded role="operator">
              <RoundsListScreen />
            </Guarded>
          }
        />

        {/* Round-scoped routes */}
        <Route
          path="/rounds/:roundId"
          element={
            <Guarded role="reviewer">
              <RoundDashboardScreen />
            </Guarded>
          }
        />
        {/* The whole-round expediente browser lives in the Tablero
            (/rounds/:roundId); the standalone review queue was retired. The
            detail route stays under /review/:expedienteId. */}
        <Route
          path="/rounds/:roundId/review/:expedienteId"
          element={
            <Guarded role="reviewer">
              <ExpedienteDetailScreen />
            </Guarded>
          }
        />
        <Route
          path="/rounds/:roundId/operations"
          element={
            <Guarded role="operator">
              <OperationsScreen />
            </Guarded>
          }
        />
        <Route
          path="/rounds/:roundId/monitor"
          element={
            <Guarded role="operator">
              <MonitorScreen />
            </Guarded>
          }
        />
        <Route
          path="/rounds/:roundId/exports"
          element={
            <Guarded role="operator">
              <ExportsScreen />
            </Guarded>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
